import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";

export const SUPPORT_STATUSES = {
  OPEN: "open",
  AWAITING_SUPPORT: "awaiting_support",
  AWAITING_USER: "awaiting_user",
  RESOLVED: "resolved",
  CLOSED: "closed",
};

export const SUPPORT_STATUS_LABELS = {
  [SUPPORT_STATUSES.OPEN]: "Open",
  [SUPPORT_STATUSES.AWAITING_SUPPORT]: "Awaiting Support",
  [SUPPORT_STATUSES.AWAITING_USER]: "Awaiting User",
  [SUPPORT_STATUSES.RESOLVED]: "Resolved",
  [SUPPORT_STATUSES.CLOSED]: "Closed",
};

export const SUPPORT_CATEGORIES = [
  "Billing",
  "Generation",
  "Account",
  "Bug",
  "Feature Request",
  "Other",
];

export const SUPPORT_PRIORITIES = ["Low", "Normal", "High"];

export function subscribeUserTickets({ db, userId, onData, onError }) {
  const ticketsRef = collection(db, "support_tickets");
  const ticketsQuery = query(
    ticketsRef,
    where("userId", "==", userId),
    orderBy("updatedAt", "desc")
  );
  return onSnapshot(ticketsQuery, onData, onError);
}

export function subscribeAdminTickets({ db, statusFilter, onData, onError }) {
  const ticketsRef = collection(db, "support_tickets");
  const baseQuery = statusFilter
    ? query(
        ticketsRef,
        where("status", "==", statusFilter),
        orderBy("updatedAt", "desc")
      )
    : query(ticketsRef, orderBy("updatedAt", "desc"));
  return onSnapshot(baseQuery, onData, onError);
}

export function subscribeTicketMessages({ db, ticketId, onData, onError }) {
  const messagesRef = collection(db, "support_tickets", ticketId, "messages");
  const messagesQuery = query(messagesRef, orderBy("createdAt", "asc"));
  return onSnapshot(messagesQuery, onData, onError);
}

export function subscribeAdminNotes({ db, ticketId, onData, onError }) {
  const notesRef = collection(db, "support_tickets", ticketId, "admin_notes");
  const notesQuery = query(notesRef, orderBy("createdAt", "desc"));
  return onSnapshot(notesQuery, onData, onError);
}

export async function createSupportTicket({
  db,
  userId,
  userEmail,
  userDisplayName,
  subject,
  category,
  priority,
  message,
}) {
  const ticketsRef = collection(db, "support_tickets");
  const ticketRef = await addDoc(ticketsRef, {
    userId,
    userEmail: userEmail || "",
    userDisplayName: userDisplayName || "",
    subject,
    category: category || "",
    priority: priority || "Normal",
    status: SUPPORT_STATUSES.OPEN,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastMessageAt: serverTimestamp(),
    lastMessageBy: "user",
    assignedAdminId: "",
    resolvedAt: null,
    closedAt: null,
  });

  await addDoc(collection(ticketRef, "messages"), {
    authorType: "user",
    authorId: userId,
    body: message,
    createdAt: serverTimestamp(),
  });

  return ticketRef.id;
}

export async function addSupportMessage({
  db,
  ticketId,
  authorType,
  authorId,
  body,
  status,
}) {
  const ticketRef = doc(db, "support_tickets", ticketId);
  await addDoc(collection(ticketRef, "messages"), {
    authorType,
    authorId,
    body,
    createdAt: serverTimestamp(),
  });

  const updatePayload = {
    updatedAt: serverTimestamp(),
    lastMessageAt: serverTimestamp(),
    lastMessageBy: authorType,
  };

  if (status) {
    updatePayload.status = status;
    updatePayload.resolvedAt =
      status === SUPPORT_STATUSES.RESOLVED ? serverTimestamp() : null;
    updatePayload.closedAt =
      status === SUPPORT_STATUSES.CLOSED ? serverTimestamp() : null;
    if (
      status === SUPPORT_STATUSES.OPEN ||
      status === SUPPORT_STATUSES.AWAITING_SUPPORT ||
      status === SUPPORT_STATUSES.AWAITING_USER
    ) {
      updatePayload.resolvedAt = null;
      updatePayload.closedAt = null;
    }
  }

  await updateDoc(ticketRef, updatePayload);
}

export async function updateSupportTicketStatus({
  db,
  ticketId,
  status,
  actorType,
  assignedAdminId,
}) {
  const ticketRef = doc(db, "support_tickets", ticketId);
  const updatePayload = {
    status,
    updatedAt: serverTimestamp(),
    lastMessageAt: serverTimestamp(),
    lastMessageBy: actorType,
  };

  if (typeof assignedAdminId === "string") {
    updatePayload.assignedAdminId = assignedAdminId;
  }

  if (status === SUPPORT_STATUSES.RESOLVED) {
    updatePayload.resolvedAt = serverTimestamp();
    updatePayload.closedAt = null;
  } else if (status === SUPPORT_STATUSES.CLOSED) {
    updatePayload.closedAt = serverTimestamp();
    updatePayload.resolvedAt = null;
  } else {
    updatePayload.resolvedAt = null;
    updatePayload.closedAt = null;
  }

  await updateDoc(ticketRef, updatePayload);
}

export async function addAdminNote({ db, ticketId, authorId, body }) {
  const notesRef = collection(db, "support_tickets", ticketId, "admin_notes");
  await addDoc(notesRef, {
    authorId,
    body,
    createdAt: serverTimestamp(),
  });
}
